import { apolloClient } from "@/apollo/client";
import {
  AlertsDocument,
  MarkAlertsReadDocument,
  type AlertsQuery,
  type AlertsQueryVariables,
  type MarkAlertsReadMutation,
  type MarkAlertsReadMutationVariables,
} from "@/generated/graphql";
import type { AlertsState } from "./alertsReducer";

export const READ_ALL_ALERTS = "READ_ALL_ALERTS";
export const MARK_AS_READ_ALERTS = "MARK_AS_READ_ALERTS";
export const READING_ALERTS = "READING_ALERTS";
export const SET_ALERTS_USER_ID = "SET_ALERTS_USER_ID";
export const UPDATE_NEW_ALERTS = "UPDATE_NEW_ALERTS";
export const UPDATE_ALERT_COUNTS = "UPDATE_ALERT_COUNTS";

// One grouped alert card, derived from the query rather than hand-written so it
// tracks codegen.
type AlertsResult = NonNullable<AlertsQuery["alerts"]>;
export type AlertCard = NonNullable<NonNullable<AlertsResult["alerts"]>[number]>;

export type AlertCounts = {
  totalAlertsCount: number;
  totalUnreadAlertsCount: number;
};

export type AlertList = AlertCounts & {
  alerts: AlertCard[];
};

// The store is still plain JS, so there is no generated RootState to lean on.
// These cover only what these thunks actually touch.
type AlertsGetState = () => { alertsReducer: AlertsState };
type AlertsDispatch = (action: unknown) => unknown;

// Alerts now come from the monolith's `alerts` GraphQL query (folded in from
// the retired notification service). The server derives the recipient from the
// authenticated user, so no user id is sent; the `userId` params are kept only
// to preserve the thunk signatures their callers already use.
const fetchAlertsPage = (page: number, pageSize: number) =>
  apolloClient.query<AlertsQuery, AlertsQueryVariables>({
    query: AlertsDocument,
    variables: { page, pageSize },
    fetchPolicy: "no-cache",
  });

const toPayload = (data: AlertsQuery | undefined | null): AlertList => {
  const result = data?.alerts;
  return {
    // `alerts` is a nullable list of nullable cards only because Absinthe makes
    // both nullable by default; list_alerts/3 never emits a nil entry. Dropping
    // them at the boundary keeps every downstream consumer free of the case.
    alerts: (result?.alerts ?? []).filter(
      (alert): alert is AlertCard => alert !== null
    ),
    totalAlertsCount: result?.totalAlertsCount ?? 0,
    totalUnreadAlertsCount: result?.totalUnreadAlertsCount ?? 0,
  };
};

export const fetchAlerts =
  (_userId?: number, page = 1, limit = 10) =>
  (dispatch: AlertsDispatch) => {
    dispatch({ type: READING_ALERTS });
    return fetchAlertsPage(page, limit)
      .then(({ data }) =>
        dispatch({ type: READ_ALL_ALERTS, payload: toPayload(data) })
      )
      .catch((err) => err);
  };

export const fetchNewAlerts =
  (_userId?: number, page = 1, limit = 10) =>
  (dispatch: AlertsDispatch) => {
    dispatch({ type: READING_ALERTS });
    return fetchAlertsPage(page, limit)
      .then(({ data }) =>
        dispatch({ type: UPDATE_NEW_ALERTS, payload: toPayload(data) })
      )
      .catch((err) => err);
  };

// Response to a `create:alert` ping. The badge only needs the counts, so this
// deliberately leaves the loaded list untouched — UPDATE_NEW_ALERTS would
// replace it with a single page and truncate a reader who had scrolled — and
// skips READING_ALERTS so a background refresh cannot flash a spinner.
//
// It reuses the `alerts` query at its smallest page rather than adding a
// counts-only query: list_alerts/3 groups and enriches every card before it
// paginates, so the page size does not change the server's work. The one card
// that comes back is discarded.
export const refreshAlertCounts = () => (dispatch: AlertsDispatch) =>
  fetchAlertsPage(1, 1)
    .then(({ data }) => {
      const { totalAlertsCount, totalUnreadAlertsCount } = toPayload(data);
      dispatch({
        type: UPDATE_ALERT_COUNTS,
        payload: { totalAlertsCount, totalUnreadAlertsCount },
      });
    })
    .catch((err) => err);

export const fetchInitialUserAlerts =
  (userId: number) => (dispatch: AlertsDispatch, getState: AlertsGetState) => {
    const { alertsReducer } = getState();
    if (userId !== alertsReducer.userId) {
      dispatch({ type: SET_ALERTS_USER_ID, payload: userId });
      dispatch(fetchAlerts(userId));
    }
  };

export const markAsReadAlerts = (_userId?: number) => (dispatch: AlertsDispatch) =>
  apolloClient
    .mutate<MarkAlertsReadMutation, MarkAlertsReadMutationVariables>({
      mutation: MarkAlertsReadDocument,
      fetchPolicy: "no-cache",
    })
    .then(() => dispatch({ type: MARK_AS_READ_ALERTS }))
    .catch((err) => err);
